import { fetchZips } from '@/components/Map/helpers';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Typography } from '@mui/material';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import MapGl, { Layer, Source } from 'react-map-gl';
import { dimensionLabel, isConfirmedNonUsCountry, isPlaceholderDimension, num } from '../format';
import { COLORS } from '../theme';
import MapHoverTooltip from './MapHoverTooltip';
import MapLegend from './MapLegend';
import WidgetSkeleton from './WidgetSkeleton';
import { buildFillPaint } from './geoFillLayer';
import {
  type FocusRequest,
  computeAutoFocusViewState,
  computeClickFocusViewState,
  createClickFocusTracker,
  createFocusTracker,
} from './mapViewport';

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
// Same default camera as the legacy Mapbox maps (constants/geo.js
// INITIAL_VIEW_STATE), minus its width/height fields — sizing here comes
// from the container Box, and react-map-gl's ViewState type doesn't have them.
const INITIAL_VIEW_STATE = { latitude: 38, longitude: -97, zoom: 3, bearing: 0, pitch: 0 };
const MAX_ZIP_CODES = 100;

/**
 * ZIP map — a real Mapbox GL choropleth using the exact same boundary
 * source the legacy Simplifi/GAM reporting tab already ships in production:
 * a live per-request call to boundaries.io (fetchZips, components/Map/
 * helpers.js), not our own /geo/boundaries endpoint — that endpoint refuses
 * ZIP outright (it's never been seeded into the Geography table). See
 * DmaChoropleth for the rest of the rationale (chartjs-plugin-zoom doesn't
 * support chartjs-chart-geo's projection scale; Mapbox has zoom/pan/pinch
 * built in).
 *
 * Capped at 100 distinct ZIPs, matching CityChoropleth's own cap — a report
 * can return far more distinct ZIPs than DMA/City ever would, and this is a
 * live call to a rate-limited third-party API on every widget load. A caption
 * below the map discloses the cap once it actually bites (review, Lucas).
 */
interface ZipFeature {
  type: 'Feature';
  properties: { zipCode?: string; value?: number | null };
  geometry: unknown;
}

/**
 * Per-ZIP label, keyed off the first (highest-impression, given desc-sorted
 * input) row that actually carries `nameKey`. A caller that queries the raw
 * ZIP alone (flank#4811, flank#4812) never sets `nameKey` on its rows at
 * all — those are skipped rather than resolving to "Unmapped" via
 * `dimensionLabel(undefined)`, so the tooltip below falls back to the bare
 * code instead.
 */
export const buildZipLabels = (rows: ReportRow[], labelKey: string, nameKey: string): Map<string, string> => {
  const labelByZip = new Map<string, string>();
  for (const row of rows) {
    const zip = String(row[labelKey] ?? '').trim();
    if (zip && !labelByZip.has(zip) && row[nameKey] !== undefined) labelByZip.set(zip, dimensionLabel(row[nameKey]));
  }
  return labelByZip;
};

/**
 * Per-ZIP value, summed rather than overwritten (flank#4811): a ZIP with more
 * than one historical city/state spelling used to arrive as multiple rows
 * sharing the same raw code, and the old `.set()` let whichever row sorted
 * last silently win instead of the real total. Callers now query the raw ZIP
 * alone so this is usually one row per ZIP already — the sum is a safety net.
 */
export const sumValuesByZip = (rows: ReportRow[], labelKey: string, valueKey: string): Map<string, number> => {
  const valueByZip = new Map<string, number>();
  for (const row of rows) {
    const zip = String(row[labelKey] ?? '').trim();
    const value = Number(row[valueKey] ?? 0);
    if (zip && Number.isFinite(value) && value > 0) valueByZip.set(zip, (valueByZip.get(zip) ?? 0) + value);
  }
  return valueByZip;
};

// boundaries.io only knows US 5-digit ZIPs, so a Canadian postcode, a 4-digit
// AdsWizz code or a placeholder would only waste one of the capped lookups.
export const isUsZipCode = (code: string): boolean => /^\d{5}$/.test(code);

interface IProps {
  rows: ReportRow[];
  /** The widget's true distinct-row count, independent of the page `rows` holds — lets the cap disclosure below say how many ZIPs are actually missing instead of just that some are (review, Lucas). */
  totalRows?: number;
  labelKey?: string;
  /** Labelled ZIP ("84121 | Salt Lake City, UT"), for the hover tooltip only — bare codes were unreadable (FB-006), same reason the ranked list below uses it. */
  nameKey?: string;
  valueKey?: string;
  /**
   * flank#4813 — a non-US postcode that happens to equal a US ZIP code was
   * drawn as that US ZIP. Requesting this alongside `labelKey` lets a
   * positively-identified non-US row get dropped before it reaches boundary
   * matching below. Optional: a caller that doesn't fetch `geo_country` just
   * never has anything to filter, same as before this fix.
   */
  countryKey?: string;
  metricLabel?: string;
  formatValue?: (v: number) => string;
  /** flank#4755 — the raw ZIP code of a row clicked in the ranked list below;
   * the map zooms to it. Null/cleared is a no-op, not a reset to default.
   * `requestId` changes on every click (even a repeat click on the same row)
   * so a dataset change can't replay a stale selection over the new
   * auto-focus (review, CodeRabbit on #4777). */
  focusRequest?: FocusRequest | null;
}

const ZipChoropleth = ({
  rows,
  totalRows = 0,
  labelKey = 'geo_zip',
  nameKey = 'geo_zip_label',
  valueKey = 'impressions',
  countryKey = 'geo_country',
  metricLabel = 'Impressions',
  formatValue = num,
  focusRequest = null,
}: IProps) => {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hover, setHover] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  const [boundaries, setBoundaries] = useState<ZipFeature[] | null>(null);
  const [failed, setFailed] = useState(false);
  // flank#4748 — "follow legacy" AND re-centre on every filter change (not
  // just initial mount): tracks the last `features` array auto-focused for,
  // so it fires once per genuinely new dataset (rows and/or boundaries
  // actually changed) but never fights a pan/zoom-only re-render, since
  // `features` below is memoized and so stays reference-equal across those.
  const focusTrackerRef = useRef(createFocusTracker<ZipFeature>());
  const clickFocusTrackerRef = useRef(createClickFocusTracker());

  // isPlaceholderDimension (not just the Unmapped check) so DV360's literal
  // "Unknown" — the #1 non-null code at 18.3M imps/30d — doesn't take one of
  // the 100 slots on every load (review, Lucas). isConfirmedNonUsCountry
  // (flank#4813) drops a row whose country is a positively-identified non-US
  // value before it can reach boundary-matching below — a non-US postcode
  // that happens to equal a US ZIP code was otherwise drawn as that US ZIP.
  const realRows = useMemo(
    () => rows.filter(row => !isPlaceholderDimension(row[labelKey]) && !isConfirmedNonUsCountry(row[countryKey])),
    [rows, labelKey, countryKey],
  );
  const allCodes = Array.from(new Set(realRows.map(row => String(row[labelKey] ?? '').trim()).filter(isUsZipCode)));
  const codes = allCodes.slice(0, MAX_ZIP_CODES);
  const codesKey = codes.join(',');

  // Disclosure for the 100-ZIP cap (review, Lucas): unfiltered over 30 days
  // the map covers a small slice of real-ZIP delivery and silently reads the
  // other 60,000+ ZIPs as zero. `totalRows` is the TRUE distinct-row count
  // (unlike `rows`, already capped to this page by the query's own `limit`),
  // minus however many placeholder rows this page turned up — an estimate,
  // not exact, but placeholders (GAM nulls, DV360 "Unknown") rank at the top
  // by impressions and so are reliably visible within the first page. A row
  // count ("top 100 of N") rather than a %-of-impressions figure: the real
  // share needs a true aggregate query this widget doesn't have (Lucas's own
  // 13% came from a one-off Snowflake profile, not from app data).
  //
  // `codes.length`, not MAX_ZIP_CODES (review, CodeRabbit): placeholder rows
  // can occupy some of this page's 100 slots, so `codes` — deduped, real
  // codes only — can fall short of the cap even while `capped` is true. The
  // caption must say how many ZIPs are actually on the map, not the
  // configured ceiling.
  const totalRealZips = Math.max(0, totalRows - (rows.length - realRows.length));
  const capped = totalRealZips > MAX_ZIP_CODES;
  const labelByZip = useMemo(() => buildZipLabels(realRows, labelKey, nameKey), [realRows, labelKey, nameKey]);

  useEffect(() => {
    setFailed(false);
    if (codes.length === 0) {
      setBoundaries([]);
      return;
    }
    // A new lookup set otherwise renders against the PREVIOUS one's
    // boundaries until this resolves — wrong ZIPs matched to new rows.
    setBoundaries(null);
    let cancelled = false;
    fetchZips(codes)
      .then((features: ZipFeature[]) => {
        if (!cancelled) setBoundaries(features);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey]);

  // Memoized on the actual data inputs (not recomputed fresh every render) so
  // its reference only changes when rows or boundaries genuinely change — the
  // signal computeAutoFocusViewState below uses to tell a filter-driven
  // refresh apart from an unrelated re-render (pan, hover). Declared before
  // the early returns below (Rules of Hooks); guards for `boundaries` not yet
  // having resolved internally.
  const valueByZip = useMemo(() => sumValuesByZip(realRows, labelKey, valueKey), [realRows, labelKey, valueKey]);
  const features = useMemo<ZipFeature[]>(() => {
    if (!boundaries) return [];
    return boundaries.map(f => {
      const zip = String(f.properties?.zipCode ?? '').trim();
      return { ...f, properties: { ...f.properties, value: valueByZip.get(zip) ?? null } };
    });
  }, [boundaries, valueByZip]);
  const scaleMax = useMemo(() => Math.max(0, ...Array.from(valueByZip.values())), [valueByZip]);

  // flank#4748: fires only when `features` is a new reference (rows and/or
  // boundaries actually changed, e.g. a filter change landed new data) —
  // skipped on a re-render carrying the SAME features (pan, hover), so the
  // user's own interaction is never fought. In a useEffect (code review,
  // #0000), not the render body: computeAutoFocusViewState mutates the
  // tracker ref as a side effect, and running that during the render phase
  // meant a discarded render (StrictMode double-invoke, an interrupted
  // concurrent render) could consume the tracker without its setViewState
  // ever committing, silently eating the auto-focus for the "real" render.
  // Declared before the early returns below (Rules of Hooks).
  useEffect(() => {
    const autoFocusedViewState = computeAutoFocusViewState(focusTrackerRef.current, features, INITIAL_VIEW_STATE);
    if (autoFocusedViewState) setViewState(autoFocusedViewState);
  }, [features]);

  // flank#4755 — depends on `features` too (unlike the auto-focus effect
  // above) so a click that lands before the ZIP boundary fetch resolves
  // still focuses once the matching feature actually appears.
  useEffect(() => {
    const clickedViewState = computeClickFocusViewState(clickFocusTrackerRef.current, features, focusRequest, f =>
      String(f.properties?.zipCode ?? ''),
    );
    if (clickedViewState) setViewState(clickedViewState);
  }, [focusRequest, features]);

  if (failed) {
    return (
      <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
        Couldn't load the ZIP boundaries. The ranked list below still reflects the data.
      </Typography>
    );
  }
  if (!boundaries) return <WidgetSkeleton height={360} />;

  const geojson = { type: 'FeatureCollection' as const, features };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: 360, width: '100%', position: 'relative' }}>
        <MapGl
          {...viewState}
          style={{ width: '100%', height: '100%' }}
          onMove={e => setViewState(e.viewState)}
          mapStyle='mapbox://styles/mapbox/light-v9'
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          interactiveLayerIds={['data']}
          onMouseMove={e => {
            const feature = e.features?.[0] as ZipFeature | undefined;
            const value = feature?.properties?.value;
            if (!feature || value == null) {
              setHover(null);
              return;
            }
            const zip = feature.properties?.zipCode ?? '';
            setHover({ x: e.point.x, y: e.point.y, label: labelByZip.get(zip) ?? zip, value });
          }}
          onMouseLeave={() => setHover(null)}
        >
          <Source type='geojson' data={geojson}>
            <Layer id='data' type='fill' paint={buildFillPaint(features) as any} />
          </Source>
        </MapGl>
        {hover && (
          <MapHoverTooltip
            x={hover.x}
            y={hover.y}
            label={hover.label}
            metricLabel={metricLabel}
            value={formatValue(hover.value)}
          />
        )}
      </Box>
      <MapLegend max={scaleMax} />
      <Typography variant='body2' sx={{ color: COLORS.muted, fontSize: 13 }}>
        {capped ? `Map shows the top ${codes.length.toLocaleString()} of ${totalRealZips.toLocaleString()} ZIPs. ` : ''}
        At this zoom, ZIP shapes are small — zoom or scroll in to see individual boundaries.
      </Typography>
    </Box>
  );
};

export default ZipChoropleth;
